import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, MapPin, Plus, RefreshCw, Warehouse } from 'lucide-react';
import { PageHead } from '../components/bits.jsx';
import { AddSiteModal } from '../components/AddSiteModal.jsx';
import { Badge, Button, Card, CardBody, CardHead, EmptyState, Loading, SearchInput, Select, Stat, Switch, useToast } from '../components/ui.jsx';
import { useApi, useDebounced } from '../lib/hooks.js';
import { compactMoney, money, num } from '../lib/format.js';

const SORTS = [
  { value: 'open:desc', label: 'Open work' },
  { value: 'jobs:desc', label: 'Total jobs' },
  { value: 'revenue:desc', label: 'Revenue · 30d' },
  { value: 'wait:desc', label: 'Longest dock wait' },
  { value: 'name:asc', label: 'Name' },
];

export default function Sites() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 300);
  const [addOpen, setAddOpen] = useState(false);

  const sort = params.get('sort') || 'open:desc';
  const customerId = params.get('customerId') || '';
  const appointmentOnly = params.get('appointment') === 'true';
  const [sortKey, sortDir] = sort.split(':');

  const filters = useMemo(
    () => ({
      sort: sortKey,
      dir: sortDir,
      q: debounced || undefined,
      customerId: customerId || undefined,
      appointment: appointmentOnly ? 'true' : undefined,
      limit: 200,
    }),
    [sortKey, sortDir, debounced, customerId, appointmentOnly],
  );

  const { data, loading, refresh } = useApi('/sites', { params: filters });
  const { data: customers } = useApi('/customers', { params: { limit: 200 } });
  const sites = data || [];

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const totalOpen = sites.reduce((s, site) => s + Number(site.openJobs || 0), 0);
  const avgWait = sites.reduce((s, site) => s + Number(site.avgWait || 0), 0) / Math.max(sites.length, 1);

  return (
    <>
      <PageHead
        title="Warehouses & docks"
        subtitle="Every place we send lumpers, and the little things that slow them down."
        icon={Warehouse}
        actions={
          <>
            <Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Refresh</Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setAddOpen(true)}>Add a place</Button>
          </>
        }
      />

      <div className="grid grid-4 mb-20">
        <Stat label="Facilities" icon={Warehouse} value={num(sites.length)} tone="amber" foot={`${sites.filter((s) => s.appointmentRequired).length} require appointments`} />
        <Stat label="Open work here" icon={Clock} value={num(totalOpen)} tone={totalOpen ? 'red' : 'lime'} foot="across all docks" />
        <Stat label="Avg dock wait" icon={Clock} value={Math.round(avgWait)} unit="min" tone={avgWait > 45 ? 'red' : 'amber'} foot="reported by crews" />
        <Stat
          label="Revenue · 30d"
          icon={MapPin}
          value={compactMoney(sites.reduce((s, site) => s + Number(site.revenue30d || 0), 0))}
          tone="green"
          foot="from these facilities"
        />
      </div>

      <div className="filter-bar">
        <SearchInput value={query} onChange={setQuery} placeholder="Facility, city, ZIP, customer…" style={{ minWidth: 240, maxWidth: 320 }} />
        <Select
          className="select-sm"
          value={customerId}
          onChange={(e) => setParam('customerId', e.target.value)}
          options={[{ value: '', label: 'Any customer' }, ...(customers || []).map((c) => ({ value: c.id, label: c.name }))]}
        />
        <Switch label="Appointment only" checked={appointmentOnly} onChange={(e) => setParam('appointment', e.target.checked ? 'true' : '')} />
        <div className="grow" />
        <Select className="select-sm" value={sort} onChange={(e) => setParam('sort', e.target.value)} options={SORTS} />
      </div>

      {loading && !sites.length ? (
        <Loading rows={8} />
      ) : sites.length ? (
        <div className="grid grid-auto-lg stagger">
          {sites.map((site) => (
            <Card key={site.id} className="tile" style={{ padding: 0 }}>
              <CardBody>
                <div className="row between gap-12">
                  <div className="grow truncate">
                    <Link to={`/app/sites/${site.id}`} className="h4" style={{ fontWeight: 640, fontSize: 'var(--fs-lg)' }}>
                      {site.name}
                    </Link>
                    <div className="dim truncate" style={{ fontSize: 'var(--fs-sm)' }}>{site.customerName || 'No customer linked'}</div>
                  </div>
                  {site.openJobs ? <Badge tone="amber" dot>{site.openJobs} open</Badge> : <Badge tone="green" size="sm" dot>clear</Badge>}
                </div>

                <div className="dim mt-8" style={{ fontSize: 'var(--fs-sm)' }}>
                  {site.address}
                  <br />
                  {site.city}, {site.state} {site.zip}
                </div>

                <div className="divider" />

                <div className="row gap-16 wrap" style={{ fontSize: 'var(--fs-xs)' }}>
                  <span className="dim">Dock {site.dockHours}</span>
                  <span className="dim">{site.dockDoors} doors</span>
                  <span className="dim">{site.appointmentRequired ? 'Appointment' : 'Drop in'}</span>
                  {site.avgWait ? <span className="dim">{Math.round(site.avgWait)} min wait</span> : null}
                  {site.avgHours ? <span className="dim">{Number(site.avgHours).toFixed(1)} hr avg</span> : null}
                  {!site.lat || !site.lng ? (
                    <span className="dim" style={{ color: 'var(--amber)' }}>Not on the map yet</span>
                  ) : String(site.geoNote || '').startsWith('Roughly') ? (
                    <span className="dim" title={site.geoNote}>Pin is approximate</span>
                  ) : null}
                </div>

                <div className="row between mt-12">
                  <span className="label">{site.code}</span>
                  <span className="num" style={{ fontSize: 'var(--fs-sm)' }}>{money(site.revenue30d)}</span>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      ) : (
        <Card><EmptyState icon={Warehouse} title="No facilities match this view" /></Card>
      )}

      <AddSiteModal
        open={addOpen}
        customers={customers || []}
        onClose={() => setAddOpen(false)}
        onCreated={(site) => {
          toast.ok(`${site.name} added`);
          refresh();
          navigate(`/app/sites/${site.id}`);
        }}
      />
    </>
  );
}
